/**
 * ReleaseCard Component
 * 
 * A single release card showing:
 * - Version badge (v1, v2, etc)
 * - Description with author
 * - Timestamp (relative or absolute)
 * - "Current" indicator for active deployment
 * - Expandable file changes tree
 */

import { Release, ReleaseDetailResponse, FileChangeNode } from '../../../../types/AppPanel';
import FileChangesTree from './FileChangesTree';

interface ReleaseCardProps {
  /** Release data */
  release: Release;
  /** Whether this is the current active deployment */
  isCurrent: boolean;
  /** Whether this card is expanded */
  isExpanded: boolean;
  /** Whether detail is loading */
  isLoadingDetail: boolean;
  /** Release detail data (when expanded) */
  detail: ReleaseDetailResponse | null;
  /** Toggle expand/collapse */
  onToggle: () => void;
  /** View file callback */
  onViewFile: (node: FileChangeNode) => void;
  /** View diff callback (for modified files) */
  onViewDiff?: (node: FileChangeNode) => void;
}

/**
 * Format date to relative or absolute string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes <= 1 ? 'Just now' : `${diffMinutes} minutes ago`;
    }
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  // Format as date with time
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Parse description to extract author email
 * Original: "Dashboard upload by email@example.com at Sun Nov 30 2025..."
 * Returns: { author, shortDesc }
 */
function parseDescription(description: string): { author: string; shortDesc: string } {
  const match = description.match(/by\s+([^\s]+@[^\s]+)/i);
  const author = match ? match[1] : 'Unknown';
  
  return {
    author,
    shortDesc: `Deployed by ${author}`,
  };
}

/**
 * Count file changes in tree recursively
 */
function countChanges(nodes: FileChangeNode[]): { added: number; modified: number; deleted: number; total: number } {
  let added = 0, modified = 0, deleted = 0;
  
  function traverse(nodes: FileChangeNode[]) {
    for (const node of nodes) {
      if (node.type === 'green-file') added++;
      else if (node.type === 'yellow-file') modified++;
      else if (node.type === 'red-file') deleted++;
      
      if (node.children) traverse(node.children);
    }
  }
  
  traverse(nodes);
  return { added, modified, deleted, total: added + modified + deleted };
}

export default function ReleaseCard({
  release,
  isCurrent,
  isExpanded,
  isLoadingDetail,
  detail,
  onToggle,
  onViewFile,
  onViewDiff,
}: ReleaseCardProps) {
  const { shortDesc } = parseDescription(release.description);
  const formattedDate = formatDate(release.deployedAt);
  
  // Count changes if expanded
  const changes = detail?.changes?.tree ? countChanges(detail.changes.tree) : null;

  return (
    <div className={`
      rounded-lg border transition-all duration-200
      ${isCurrent 
        ? 'bg-emerald-500/5 border-emerald-500/30' 
        : 'bg-white/[0.02] border-white/10 hover:border-white/20'}
    `}>
      {/* Main Row */}
      <div
        className="flex items-start gap-4 p-4 cursor-pointer"
        onClick={onToggle}
      >
        {/* Status Indicator (green dot for current, gray for past) */}
        <div className={`
          w-3 h-3 rounded-full mt-1.5 flex-shrink-0
          ${isCurrent ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30' : 'bg-white/20'}
        `} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Version + Current Badge */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base font-semibold text-white">
              {release.releaseName}
            </span>
            {isCurrent && (
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-emerald-500/20 text-emerald-400">
                Current
              </span>
            )}
          </div>

          {/* Description */}
          <p className="text-sm text-white/60 truncate mb-2">
            {shortDesc}
          </p>

          {/* Timestamp */}
          <div className="flex items-center gap-2 text-xs text-white/40">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <span>{formattedDate}</span>
          </div>
        </div>

        {/* Expand/Collapse Button */}
        <button 
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-white/50 hover:text-white/70 hover:bg-white/5 transition-colors flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <span>{isExpanded ? 'Hide' : 'View'} changes</span>
          <svg 
            width="12" 
            height="12" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          >
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0">
          <div className="ml-7 border-t border-white/5 pt-4">
            {isLoadingDetail ? (
              // Loading state
              <div className="flex items-center gap-2 text-white/40 text-sm py-4">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Loading changes...</span>
              </div>
            ) : detail?.changes?.tree && detail.changes.tree.length > 0 ? (
              <>
                {/* Changes Summary */}
                {changes && changes.total > 0 && (
                  <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
                    <span className="text-white/40">
                      {changes.total} {changes.total === 1 ? 'file' : 'files'} changed
                    </span>
                    {changes.added > 0 && (
                      <span className="flex items-center gap-1 text-emerald-400">
                        <span className="font-mono font-bold">+</span>
                        {changes.added} added
                      </span>
                    )}
                    {changes.modified > 0 && (
                      <span className="flex items-center gap-1 text-amber-400">
                        <span className="font-mono font-bold">~</span>
                        {changes.modified} modified
                      </span>
                    )}
                    {changes.deleted > 0 && (
                      <span className="flex items-center gap-1 text-red-400">
                        <span className="font-mono font-bold">-</span>
                        {changes.deleted} deleted
                      </span>
                    )}
                  </div>
                )}

                {/* File Tree */}
                <FileChangesTree
                  nodes={detail.changes.tree}
                  onViewFile={onViewFile}
                  onViewDiff={onViewDiff}
                />
              </>
            ) : (
              // No changes
              <p className="text-white/40 text-sm py-4">No file changes in this release</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

